import { Component } from 'react';
import { DateRangePicker } from 'react-dates';
import { styled } from '@mui/system';

import 'react-dates/initialize';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { themeColors } from '../../../utils/theme';

class HorizontalDateRangePicker extends Component {
    static propTypes = {
        startDate: momentPropTypes.momentObj,
        endDate: momentPropTypes.momentObj,
        dateFormat: PropTypes.string,
        offset: PropTypes.bool,
        inputWidth: PropTypes.number,
        isOutsideRange: PropTypes.bool,

        onDateRangeChange: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            startDate: props.startDate,
            endDate: props.endDate,
            focusedInput: null,
        };
    }
    
    onDatesChange(startDate, endDate) {
        const { onDateRangeChange } = this.props;
        if (onDateRangeChange) {
            onDateRangeChange(startDate, endDate);
        }
        this.setState({ startDate, endDate });
    }

    render() {
        const { dateFormat, offset, inputWidth, isOutsideRange } = this.props;
        return (
            <StyledDatePickerWrapper
                id="date_range_picker"
                $offset={offset}
                inputwidth={inputWidth}
            >
                <DateRangePicker
                    startDate={this.state.startDate}
                    startDatePlaceholderText="Start"
                    endDatePlaceholderText="End"
                    startDateId="start_date_id"
                    endDate={this.state.endDate}
                    endDateId="end_date_id"
                    onDatesChange={({ startDate, endDate }) =>
                        this.onDatesChange(startDate, endDate)
                    }
                    focusedInput={this.state.focusedInput}
                    onFocusChange={(focusedInput) => this.setState({ focusedInput })}
                    keepOpenOnDateSelect
                    hideKeyboardShortcutsPanel
                    displayFormat={dateFormat}
                    showDefaultInputIcon
                    enableOutsideDays={false}
                    isOutsideRange={isOutsideRange}
                    small
                    block
                    navPrev={<StyledChevronLeftRoundedIcon id="prev_arrow" fontSize="small" />}
                    navNext={<StyledChevronRightRoundedIcon id="next_arrow" fontSize="small" />}
                />
            </StyledDatePickerWrapper>
        );
    }
}

export default HorizontalDateRangePicker;

const StyledChevronLeftRoundedIcon = styled(ChevronLeftRoundedIcon)({
    position: 'absolute',
    left: '37%',
    top: 22,
    color: themeColors.calendarNav,
});

const StyledChevronRightRoundedIcon = styled(ChevronRightRoundedIcon)({
    position: 'absolute',
    left: '42%',
    top: 22,
    color: themeColors.calendarNav,
});

const StyledDatePickerWrapper = styled('div')(({ $offset, inputwidth }) => ({
    width: '90%',
    // The following are CSS selectors for react-dates classes
    '.CalendarDay': {
        border: 'none',
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__blocked_out_of_range, .CalendarDay__blocked_out_of_range:active, .CalendarDay__blocked_out_of_range:hover': {
        color: themeColors.calendarGrey,
    },
    '.CalendarDay__selected_start': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_start::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
    },
    '.CalendarDay__selected_end': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_end::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        right: 0,
    },
    '.CalendarDay__selected_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__selected': {
        color: 'white',
        opacity: 1,
    },
    '.CalendarDay__selected:hover': {
        background: themeColors.primaryLight,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__hovered_span:hover, .CalendarDay__hovered_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        border: 'none',
    },
    '.DateRangePickerInput__withBorder': {
        border: 'none',
    },
    '.DateInput': {
        width: inputwidth ? `${inputwidth}px` : '55px',
        textAlign: 'left',
    },
    '.DateInput_input, .DateInput_input_1': {
        fontSize: 15,
        padding: 0,
        display: 'flex',
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '24px',
    },
    '.DateInput_input__focused, .DateInput_input__focused_2': {
        borderBottom: `2px solid ${themeColors.primaryLight}`,
    },
    '.DateRangePickerInput_calendarIcon': {
        margin: 0,
        padding: 0,
        outline: 'none',
        boxShadow: 'none',
        paddingRight: 5,
    },
    '.DateRangePickerInput_calendarIcon_svg': {
        fill: themeColors.primaryLight,
        height: 15,
        width: 14,
        verticalAlign: 'middle',
        marginBottom: 5,
        marginLeft: 5,
    },
    '.DateRangePickerInput_arrow': {
        paddingRight: 5,
        color: themeColors.placeholderSearchText,
    },
    '.DayPicker': {
        right: $offset ? '140px' : null,
    },
    '.DateRangePicker_picker': {
        background: 'transparent',
    },
}));
